<script setup>
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import AuditTrailEmptyState from './components/AuditTrailEmptyState.vue';
import AuditTrailFilter from './components/AuditTrailFilter.vue';
import AuditTrailList from './components/AuditTrailList.vue';
import AuditTrailListLayout from './components/AuditTrailListLayout.vue';
import AuditTrailLoadingState from './components/AuditTrailLoadingState.vue';

const DEFAULT_SORT_FIELD = 'created_at';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const store = useStore();
const auditTrailRecords = useMapGetter('wgptAuditTrail/get');
const auditTrailMeta = useMapGetter('wgptAuditTrail/getMeta');
const uiFlags = useMapGetter('wgptAuditTrail/getUIFlags');
const storedFilters = useMapGetter('wgptAuditTrail/getAppliedFilters');

const appliedFilters = ref([]);
const showFilter = ref(false);

const sortState = reactive({
  activeSort: DEFAULT_SORT_FIELD,
  activeOrdering: '-',
});

const pageNumber = computed(() => Number(route.query?.page) || 1);
const isFetchingList = computed(() => uiFlags.value.isFetching);
const currentPage = computed(() => Number(auditTrailMeta.value.page));
const totalItems = computed(() => auditTrailMeta.value?.total);
const hasRecords = computed(() => auditTrailRecords.value.length > 0);

const hasAppliedFilters = computed(() => {
  return (
    storedFilters.value.length > 0 &&
    storedFilters.value.some(filter => {
      if (typeof filter.values === 'object') return filter.values.id !== '';
      return filter.values && filter.values !== '';
    })
  );
});

const updatePageParam = page => {
  const query = { ...route.query, page: page.toString() };
  router.replace({ query });
};

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

const getCommonFetchParams = (page = 1) => ({
  page,
  sortAttr: buildSortAttr(),
  label: undefined,
});

const transformFilters = filters => {
  return filters.filter(filter => {
    if (typeof filter.values === 'object') return filter.values.id !== '';
    return filter.values && filter.values !== '';
  });
};

const fetchAuditTrail = async (page = 1) => {
  await store.dispatch('wgptAuditTrail/clearFilters');
  await store.dispatch('wgptAuditTrail/get', getCommonFetchParams(page));
  updatePageParam(page);
};

const fetchFilteredAuditTrail = async (payload, page = 1) => {
  if (!hasAppliedFilters.value) return;

  const transformedFilters = transformFilters(payload);
  await store.dispatch('wgptAuditTrail/get', {
    ...getCommonFetchParams(page),
    filters: transformedFilters,
  });
  updatePageParam(page, '');
};

const fetchAuditTrailBasedOnContext = async page => {
  updatePageParam(page);
  if (isFetchingList.value) return;

  if (hasAppliedFilters.value) {
    await fetchFilteredAuditTrail(appliedFilters.value, page);
    return;
  }

  await fetchAuditTrail(page);
};

const isValidFilter = filter => {
  if (filter.attributeKey === 'user' && filter.values === null) return true;
  return filter.values && filter.values !== '';
};

const initializeFilters = () => {
  if (storedFilters.value?.length > 0) {
    appliedFilters.value = [...storedFilters.value];
  } else if (appliedFilters.value.length === 0) {
    appliedFilters.value = [
      {
        attributeKey: 'user',
        filterOperator: 'equal_to',
        values: { id: '', name: '' },
        queryOperator: 'and',
        attributeModel: 'standard',
      },
    ];
  }
};

const toggleFilter = () => {
  if (!showFilter.value) {
    initializeFilters();
  }
  showFilter.value = !showFilter.value;
};

const onFilterApply = filters => {
  const validFilters = filters.filter(isValidFilter);
  appliedFilters.value = validFilters;
  showFilter.value = false;

  store.dispatch('wgptAuditTrail/setAppliedFilters', validFilters);

  if (validFilters.length > 0) {
    fetchFilteredAuditTrail(validFilters, 1);
  } else {
    fetchAuditTrail(1);
  }
};

const onFilterClear = () => {
  appliedFilters.value = [];
  showFilter.value = false;
  store.dispatch('wgptAuditTrail/setAppliedFilters', []);
  fetchAuditTrail(1);
};

const onFilterClose = () => {
  showFilter.value = false;
};

onMounted(async () => {
  store.dispatch('agents/get');
  initializeFilters();
  if (hasAppliedFilters.value) {
    fetchFilteredAuditTrail(appliedFilters.value, pageNumber.value);
  } else {
    fetchAuditTrail(pageNumber.value);
  }
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-surface-1"
  >
    <AuditTrailListLayout
      :header-title="t('WGPT_AUDIT_TRAIL.HEADER.TITLE')"
      :current-page="currentPage"
      :total-items="totalItems"
      :show-pagination-footer="!isFetchingList && hasRecords"
      :active-sort="sortState.activeSort"
      :active-ordering="sortState.activeOrdering"
      :is-fetching-list="isFetchingList"
      :has-applied-filters="hasAppliedFilters"
      @update:current-page="fetchAuditTrailBasedOnContext"
      @filter="toggleFilter"
    >
      <template #filter>
        <div
          class="absolute mt-1 ltr:-right-52 rtl:-left-52 sm:ltr:right-0 sm:rtl:left-0 top-full"
        >
          <AuditTrailFilter
            v-if="showFilter"
            v-model="appliedFilters"
            @apply-filter="onFilterApply"
            @clear-filters="onFilterClear"
            @close="onFilterClose"
          />
        </div>
      </template>
      <AuditTrailLoadingState v-if="isFetchingList" />

      <AuditTrailEmptyState
        v-else-if="auditTrailRecords.length === 0"
        :has-records="hasRecords"
        :page-number="pageNumber"
        :has-applied-filters="hasAppliedFilters"
      />

      <AuditTrailList v-else :records="auditTrailRecords" />
    </AuditTrailListLayout>
  </div>
</template>
